import { useState, useEffect, useCallback } from 'react';
import { AdminSidebar } from './AdminDashboard';
import api from '../lib/api';
import { MapPin, CalendarBlank, Funnel } from '@phosphor-icons/react';

const STATUS_OPTIONS = ['', 'PENDING', 'CONFIRMED', 'IN_PROGRESS', 'DONE', 'CANCELLED'];
const STATUS_LABELS = {
  PENDING: { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400' },
  CONFIRMED: { label: 'Confirmee', color: 'bg-blue-500/20 text-blue-400' },
  IN_PROGRESS: { label: 'En cours', color: 'bg-green-500/20 text-green-400' },
  DONE: { label: 'Terminee', color: 'bg-desti-gold/20 text-desti-gold' },
  CANCELLED: { label: 'Annulee', color: 'bg-red-500/20 text-red-400' },
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // ✅ Fonction stabilisée
  const fetchBookings = useCallback(() => {
    const params = `page=${page}&limit=15${filter ? `&status=${filter}` : ''}`;
    api.get(`/admin/bookings?${params}`)
      .then(r => {
        setBookings(r.data.bookings);
        setTotalPages(r.data.pages);
      })
      .catch(() => {});
  }, [page, filter]); // ← dépendances réelles

  // ✅ Plus d'avertissement ESLint
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status`, { status });
      fetchBookings(); // fonctionne toujours
    } catch {}
  };

  return (
    <div className="pt-20 min-h-screen" data-testid="admin-bookings-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <AdminSidebar />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-heading text-3xl text-white">Reservations</h1>
              <div className="flex items-center gap-2">
                <Funnel size={16} className="text-desti-gold" />
                <select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}
                  className="bg-desti-surface border border-white/10 px-3 py-2 text-white text-sm focus:outline-none focus:border-desti-gold"
                  data-testid="booking-status-filter">
                  <option value="">Tous</option>
                  {STATUS_OPTIONS.filter(Boolean).map(s => <option key={s} value={s}>{STATUS_LABELS[s]?.label}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-3">
              {bookings.map(b => {
                const st = STATUS_LABELS[b.status] || STATUS_LABELS.PENDING;
                return (
                  <div key={b.id} className="bg-desti-surface border border-white/5 p-4" data-testid={`admin-booking-${b.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className={`text-xs px-2 py-0.5 ${st.color}`}>{st.label}</span>
                        <p className="text-xs text-white/30 mt-1">#{b.id?.slice(-8).toUpperCase()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium">{b.priceTTC?.toFixed(2)} EUR</p>
                        <p className="text-xs text-white/30">{b.contactName || b.contactEmail}</p>
                      </div>
                    </div>
                    <div className="text-xs text-white/50 space-y-1">
                      <div className="flex items-center gap-1"><MapPin size={12} className="text-desti-gold" /> {b.pickupAddress} &rarr; {b.dropoffAddress}</div>
                      <div className="flex items-center gap-1"><CalendarBlank size={12} className="text-desti-gold" /> {b.pickupDate} a {b.pickupTime} | {b.vehicleName || b.vehicleType} | {b.distanceKm} km</div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {['PENDING', 'CONFIRMED', 'IN_PROGRESS', 'DONE', 'CANCELLED'].filter(s => s !== b.status).map(s => (
                        <button key={s} onClick={() => updateStatus(b.id, s)}
                          className="text-xs px-2 py-1 border border-white/10 text-white/50 hover:text-white hover:border-desti-gold transition-colors"
                          data-testid={`set-status-${s}-${b.id}`}>
                          {STATUS_LABELS[s]?.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i} onClick={() => setPage(i + 1)}
                    className={`w-8 h-8 text-xs ${page === i + 1 ? 'bg-desti-gold text-desti-black' : 'bg-desti-surface text-white/50 border border-white/10'}`}>
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
